/**
 * Botón 🎁 en el header que abre el drawer de novedades.
 * Todos los usuarios autenticados lo ven.
 *
 * Muestra un punto durante los 3 días siguientes a la última novedad publicada.
 * No se registra en el servidor qué vio cada usuario: el punto se apaga al abrir
 * el cajón guardando en el navegador la última novedad vista. Si el navegador no
 * deja guardar (ventana privada, datos borrados), vale solo la regla de los días.
 */
import { useState } from 'react'
import { Gift } from 'lucide-react'
import { UpdatesDrawer } from './UpdatesDrawer'
import { useLatestUpdate } from '../hooks/usePlatformUpdates'

const DIAS_CON_PUNTO = 3
const CLAVE_VISTA = 'clobi-novedades-ultima-vista'

function leerVista(): string | null {
  try {
    return localStorage.getItem(CLAVE_VISTA)
  } catch {
    return null
  }
}

function guardarVista(id: string) {
  try {
    localStorage.setItem(CLAVE_VISTA, id)
  } catch {
    // Sin almacenamiento el punto se apaga solo al cumplirse los días.
  }
}

export function UpdatesTrigger() {
  const [open, setOpen] = useState(false)
  const [vista, setVista] = useState<string | null>(leerVista)
  const { data: ultima } = useLatestUpdate()

  const reciente = !!ultima
    && Date.now() - new Date(ultima.published_at).getTime() < DIAS_CON_PUNTO * 24 * 60 * 60 * 1000
  const conPunto = reciente && ultima.id !== vista

  const abrir = () => {
    setOpen(true)
    if (ultima) {
      guardarVista(ultima.id)
      setVista(ultima.id)
    }
  }

  return (
    <>
      <button
        type="button"
        aria-label={conPunto ? 'Novedades en Clobi (hay novedades recientes)' : 'Novedades en Clobi'}
        title="Novedades"
        onClick={abrir}
        className="relative p-2 rounded-lg text-content-muted hover:text-primary hover:bg-soft transition-colors"
      >
        <Gift size={18} />
        {conPunto && (
          <span
            aria-hidden="true"
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-surface"
          />
        )}
      </button>
      <UpdatesDrawer open={open} onClose={() => setOpen(false)} />
    </>
  )
}
